"use client";

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { type VariantProps, cva } from "class-variance-authority";
import { XIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "@/lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

/*
 * The scrim is a plain tint with no backdrop blur and the same 200ms as the
 * sheet. A full-viewport backdrop filter promotes the whole page into its own
 * compositing surface, and tearing that down halfway through the slide-out
 * re-rasterised the page mid-animation: the body appeared to flicker on close.
 * The dialog and alert-dialog scrims follow the same rule.
 */
function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Backdrop>) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-(--z-backdrop) bg-black/10 duration-200 dark:bg-black/40 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0 data-closed:pointer-events-none",
        className,
      )}
      {...props}
    />
  );
}

/*
 * Every right-hand sheet in the app is one of four widths. Pick the stop that
 * fits the content instead of writing a width per sheet: sm for a form or a
 * metadata list, md for a settings panel, lg for a document with tabs or a
 * thread, xl for graphs and inspectors.
 */
const sheetVariants = cva(
  "fixed z-(--z-overlay) bg-card bg-clip-padding text-sm shadow-lg ring-1 ring-foreground/10 transition ease-in-out outline-none data-closed:pointer-events-none data-closed:duration-200 data-open:duration-200 data-open:animate-in data-closed:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-closed:slide-out-to-top data-open:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-closed:slide-out-to-bottom data-open:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-closed:slide-out-to-left data-open:slide-in-from-left sm:max-w-sm",
        right:
          "bottom-2 right-2 top-2 h-[calc(100%-1rem)] rounded-lg border data-closed:slide-out-to-right data-open:slide-in-from-right",
      },
      size: {
        sm: "",
        md: "",
        lg: "",
        xl: "",
      },
    },
    compoundVariants: [
      { side: "right", size: "sm", class: "w-[min(420px,calc(100%-1rem))]" },
      { side: "right", size: "md", class: "w-[min(480px,calc(100%-1rem))]" },
      { side: "right", size: "lg", class: "w-[min(640px,calc(100%-1rem))]" },
      { side: "right", size: "xl", class: "w-[min(760px,calc(100%-1rem))]" },
    ],
    defaultVariants: {
      side: "right",
      size: "sm",
    },
  },
);

function SheetContent({
  side = "right",
  size = "sm",
  className,
  overlayClassName,
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Popup> &
  VariantProps<typeof sheetVariants> & {
    overlayClassName?: string;
  }) {
  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        data-size={size}
        className={cn(
          sheetVariants({ side, size }),
          "flex flex-col overflow-hidden",
          className,
        )}
        {...props}
      >
        {children}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

const sheetHeaderCloseClassName =
  "ml-2 mt-0.5 flex shrink-0 rounded-md p-1 text-tertiary-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";
const sheetTitleClassName = "text-base font-semibold text-foreground";
const sheetDescriptionClassName = "text-sm text-tertiary-foreground";

/** The close control's contents: the X and its accessible name. */
function SheetCloseContent() {
  return (
    <>
      <XIcon className="size-4" />
      <span className="sr-only">Close</span>
    </>
  );
}

/**
 * The header's markup with the close control as a slot. SheetHeader fills
 * the slot with the dialog's own close; a sheet drawn outside a dialog, such
 * as a static product preview, passes a plain button.
 */
function SheetHeaderView({
  className,
  children,
  actions,
  close,
  ...props
}: React.ComponentProps<"div"> & {
  actions?: React.ReactNode;
  close: React.ReactNode;
}) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "sticky top-0 z-10 flex items-start gap-2 border-b border-border bg-card px-4 py-3",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">{children}</div>
      {actions ? (
        <div className="flex items-center gap-2">{actions}</div>
      ) : null}
      {close}
    </div>
  );
}

function SheetHeader({
  className,
  children,
  actions,
  ...props
}: React.ComponentProps<"div"> & {
  actions?: React.ReactNode;
}) {
  return (
    <SheetHeaderView
      className={className}
      actions={actions}
      close={
        <SheetPrimitive.Close
          data-slot="sheet-header-close"
          className={sheetHeaderCloseClassName}
        >
          <SheetCloseContent />
        </SheetPrimitive.Close>
      }
      {...props}
    >
      {children}
    </SheetHeaderView>
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(sheetTitleClassName, className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn(sheetDescriptionClassName, className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetCloseContent,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetHeaderView,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  sheetDescriptionClassName,
  sheetHeaderCloseClassName,
  sheetTitleClassName,
  sheetVariants,
};
